import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { AudioSettings, AudioQuality, AudioDevice } from "../../types/audio";

interface AudioSettingsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  settings: AudioSettings;
  onSettingsChange: (_settings: Partial<AudioSettings>) => void;
  availableInputDevices: AudioDevice[];
  availableOutputDevices: AudioDevice[];
}

export const AudioSettingsPanel: React.FC<AudioSettingsPanelProps> = ({
  isOpen,
  onClose,
  settings,
  onSettingsChange,
  availableInputDevices,
  availableOutputDevices,
}) => {
  const [localSettings, setLocalSettings] = useState(settings);

  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleSave = () => {
    onSettingsChange(localSettings);
    onClose();
  };

  const handleCancel = () => {
    setLocalSettings(settings);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-xl font-bold text-white">Audio Settings</h2>
          <button
            onClick={handleCancel}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Input Device */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Microphone</label>
            <select
              value={localSettings.inputDevice}
              onChange={(e) => setLocalSettings({ ...localSettings, inputDevice: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="default">Default Microphone</option>
              {availableInputDevices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label}
                </option>
              ))}
            </select>
          </div>

          {/* Output Device */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Speakers/Headphones
            </label>
            <select
              value={localSettings.outputDevice}
              onChange={(e) => setLocalSettings({ ...localSettings, outputDevice: e.target.value })}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="default">Default Output</option>
              {availableOutputDevices.map((device) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label}
                </option>
              ))}
            </select>
          </div>

          {/* Audio Quality */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Audio Quality</label>
            <select
              value={localSettings.quality}
              onChange={(e) =>
                setLocalSettings({
                  ...localSettings,
                  quality: parseInt(e.target.value) as AudioQuality,
                })
              }
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={AudioQuality.LOW}>Low (16 kbps)</option>
              <option value={AudioQuality.MEDIUM}>Medium (32 kbps)</option>
              <option value={AudioQuality.HIGH}>High (64 kbps)</option>
              <option value={AudioQuality.ULTRA}>Ultra (128 kbps)</option>
            </select>
            <p className="mt-1 text-xs text-gray-400">Higher quality uses more bandwidth</p>
          </div>

          {/* Audio Processing */}
          <div className="space-y-3">
            <h3 className="text-sm font-medium text-gray-300">Audio Processing</h3>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={localSettings.noiseSuppression}
                onChange={(e) =>
                  setLocalSettings({
                    ...localSettings,
                    noiseSuppression: e.target.checked,
                  })
                }
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-300">Noise Suppression</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={localSettings.echoCancellation}
                onChange={(e) =>
                  setLocalSettings({
                    ...localSettings,
                    echoCancellation: e.target.checked,
                  })
                }
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-300">Echo Cancellation</span>
            </label>

            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={localSettings.autoGainControl}
                onChange={(e) =>
                  setLocalSettings({
                    ...localSettings,
                    autoGainControl: e.target.checked,
                  })
                }
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-300">Auto Gain Control</span>
            </label>
          </div>

          {/* Push to Talk */}
          <div className="space-y-3">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={localSettings.pushToTalk}
                onChange={(e) =>
                  setLocalSettings({
                    ...localSettings,
                    pushToTalk: e.target.checked,
                  })
                }
                className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-300">Push to Talk</span>
            </label>

            {localSettings.pushToTalk && (
              <div className="ml-7">
                <label className="block text-sm font-medium text-gray-400 mb-2">
                  Push to Talk Key
                </label>
                <select
                  value={localSettings.pushToTalkKey}
                  onChange={(e) =>
                    setLocalSettings({
                      ...localSettings,
                      pushToTalkKey: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Space">Space</option>
                  <option value="ControlLeft">Left Ctrl</option>
                  <option value="ControlRight">Right Ctrl</option>
                  <option value="AltLeft">Left Alt</option>
                  <option value="AltRight">Right Alt</option>
                </select>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-gray-700">
          <button
            onClick={handleCancel}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
          >
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};

export default AudioSettingsPanel;
